import React, { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Card from "components/card";
import { MdSearch, MdAdd, MdEdit, MdDelete, MdClose } from "react-icons/md";
import { getFeatureList, createFeature, updateFeature, deleteFeature } from "api/adapters/admin/admin";

const ICON_OPTIONS = [
  "verified", "passport", "roi", "win-rate", "best-asset",
  "trade-chart", "security", "sync", "share", "lock", "star", "graph",
];

function debounce(fn, delay) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
}

// ─── Feature Form Modal ───────────────────────────────────────────────────────
function FeatureModal({ feature, onClose, onSave, saving, error }) {
  const [sTitle, setTitle] = useState(feature?.sTitle || "");
  const [sDescription, setDescription] = useState(feature?.sDescription || "");
  const [sIconType, setIconType] = useState(feature?.sIconType || "star");
  const [nOrder, setOrder] = useState(feature?.nOrder ?? 0);
  const [eStatus, setStatus] = useState(feature?.eStatus || "Active");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sTitle.trim() || !sDescription.trim()) return;
    onSave({ sTitle: sTitle.trim(), sDescription: sDescription.trim(), sIconType, nOrder: Number(nOrder), eStatus });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white dark:bg-[#111111] shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-white/10">
          <h3 className="text-base font-bold text-navy-700 dark:text-white">
            {feature ? "Edit Feature" : "Add New Feature"}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-white">
            <MdClose className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          {error && (
            <p className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">
              {error}
            </p>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Title
            </label>
            <input
              value={sTitle}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Feature title..."
              required
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-200 dark:border-white/10 dark:bg-[#1a1a1a] dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Description
            </label>
            <textarea
              value={sDescription}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Feature description..."
              required
              rows={3}
              className="w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-200 dark:border-white/10 dark:bg-[#1a1a1a] dark:text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Icon Type
              </label>
              <select
                value={sIconType}
                onChange={(e) => setIconType(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-orange-500 dark:border-white/10 dark:bg-[#1a1a1a] dark:text-white"
              >
                {ICON_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Order
              </label>
              <input
                type="number"
                value={nOrder}
                onChange={(e) => setOrder(e.target.value)}
                min={0}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-orange-500 dark:border-white/10 dark:bg-[#1a1a1a] dark:text-white"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Status
            </label>
            <select
              value={eStatus}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-orange-500 dark:border-white/10 dark:bg-[#1a1a1a] dark:text-white"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-gray-400 dark:hover:bg-[#1a1a1a]">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 rounded-xl bg-orange-500 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60 transition-colors">
              {saving ? "Saving..." : feature ? "Update Feature" : "Add Feature"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel, loading, error }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-[#111111] p-6 shadow-2xl">
        <p className="mb-6 text-center text-sm font-medium text-navy-700 dark:text-white">{message}</p>
        {error && (
          <p className="mb-4 rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </p>
        )}
        <div className="flex gap-3">
          <button onClick={onCancel}
            className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-gray-400">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60">
            {loading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const FeaturesManagement = () => {
  const queryClient = useQueryClient();
  const [sSearch, setSearch] = useState("");
  const [nPage, setPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [modalError, setModalError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const debouncedSearch = useCallback(
    debounce((val) => { setSearch(val); setPage(1); }, 400),
    []
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["featureList", sSearch, nPage],
    queryFn: () => getFeatureList({ sSearch, nPage, nLimit: 10 }),
    keepPreviousData: true,
  });

  const list = data?.data;
  const features = list?.aFeatures ?? [];
  const nTotalPages = list?.nTotalPages ?? 1;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["featureList"] });

  const { mutate: doCreate, isPending: creating } = useMutation({
    mutationFn: createFeature,
    onSuccess: () => { invalidate(); setModal(null); setModalError(""); },
    onError: (err) => setModalError(err.message || "Failed to create feature."),
  });

  const { mutate: doUpdate, isPending: updating } = useMutation({
    mutationFn: ({ id, ...d }) => updateFeature(id, d),
    onSuccess: () => { invalidate(); setModal(null); setModalError(""); },
    onError: (err) => setModalError(err.message || "Failed to update feature."),
  });

  const { mutate: doDelete, isPending: deleting } = useMutation({
    mutationFn: () => deleteFeature(deleteId),
    onSuccess: () => { invalidate(); setDeleteId(null); setDeleteError(""); },
    onError: (err) => setDeleteError(err.message || "Failed to delete feature."),
  });

  const handleSave = (formData) => {
    setModalError("");
    if (modal?.feature) doUpdate({ id: modal.feature._id, ...formData });
    else doCreate(formData);
  };

  return (
    <div className="flex flex-col gap-5">
      <Card extra="p-5">
        {/* Header */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:w-64">
            <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search features..."
              onChange={(e) => debouncedSearch(e.target.value)}
              className="h-10 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-200 dark:border-white/10 dark:bg-[#111111] dark:text-white"
            />
          </div>
          <button
            onClick={() => setModal({})}
            className="flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 transition-colors shadow-md shadow-orange-200 dark:shadow-none"
          >
            <MdAdd className="h-5 w-5" />
            Add Feature
          </button>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-100 dark:bg-[#1a1a1a]" />
            ))}
          </div>
        ) : features.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-4xl mb-3">⚡</p>
            <p className="text-sm text-gray-400">No features found. Add your first feature.</p>
          </div>
        ) : (
          <div className={`overflow-x-auto ${isFetching ? "opacity-60" : ""}`}>
            <table className="w-full min-w-[600px] table-auto text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-white/10">
                  <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">#</th>
                  <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Title</th>
                  <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Icon</th>
                  <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Order</th>
                  <th className="pb-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Status</th>
                  <th className="pb-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-white/5">
                {features.map((feature, idx) => (
                  <tr key={feature._id} className="group">
                    <td className="py-3.5 pr-4 text-xs text-gray-400">{(nPage - 1) * 10 + idx + 1}</td>
                    <td className="py-3.5 pr-4">
                      <p className="font-semibold text-navy-700 dark:text-white">{feature.sTitle}</p>
                      <p className="mt-0.5 line-clamp-1 text-xs text-gray-400">{feature.sDescription}</p>
                    </td>
                    <td className="py-3.5 pr-4">
                      <span className="inline-flex items-center rounded-lg bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-600 dark:bg-orange-500/10 dark:text-orange-400">
                        {feature.sIconType}
                      </span>
                    </td>
                    <td className="py-3.5 pr-4 text-sm text-gray-500 dark:text-gray-400">{feature.nOrder}</td>
                    <td className="py-3.5 pr-4">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        feature.eStatus === "Active"
                          ? "bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400"
                          : "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400"
                      }`}>
                        {feature.eStatus}
                      </span>
                    </td>
                    <td className="py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setModal({ feature })}
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-orange-50 hover:text-orange-500 transition-colors dark:hover:bg-[#2a2a2a]"
                        >
                          <MdEdit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteId(feature._id)}
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors dark:hover:bg-[#2a2a2a]"
                        >
                          <MdDelete className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {nTotalPages > 1 && (
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs text-gray-500">Page {nPage} of {nTotalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={nPage === 1}
                className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-40 dark:border-white/10 dark:text-gray-400 dark:hover:bg-[#1a1a1a]">
                Previous
              </button>
              <button onClick={() => setPage((p) => Math.min(nTotalPages, p + 1))} disabled={nPage === nTotalPages}
                className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-40 dark:border-white/10 dark:text-gray-400 dark:hover:bg-[#1a1a1a]">
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {modal !== null && (
        <FeatureModal
          feature={modal.feature}
          onClose={() => { setModal(null); setModalError(""); }}
          onSave={handleSave}
          saving={creating || updating}
          error={modalError}
        />
      )}
      {deleteId && (
        <ConfirmDialog
          message="Are you sure you want to delete this feature? This cannot be undone."
          onConfirm={doDelete}
          onCancel={() => { setDeleteId(null); setDeleteError(""); }}
          loading={deleting}
          error={deleteError}
        />
      )}
    </div>
  );
};

export default FeaturesManagement;
